import { FeatureId, IOrganization, IOrganizationFeature } from '@dealpoint/types'
import { useCallback, useContext, useMemo, useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'

import { APIClient } from '../APIClient'
import { AppContext } from '../AppContext'

interface UpdateOrgProps {
  logo?: string
  name?: string
  documentTermSingular?: string
  documentTermPlural?: string
  tavusGreeting?: string
}

export default function useOrgInfo() {
  const context = useContext(AppContext)
  const { mutate: globalMutate } = useSWRConfig()
  const [saving, setSaving] = useState(false)
  const { data, error, loading, mutate } = useSWR<IOrganization>('/org/info', {
    fallbackData: context.orgInfo,
  })

  // aristotle-backend's /org/info omits `logos` (and may omit `features`), but
  // the legacy app reads orgInfo.logos.light unconditionally. Normalize so the
  // shape always has the fields the UI expects.
  const orgInfo = useMemo<IOrganization>(
    () =>
      ({
        logos: {},
        features: [],
        ...(data as object),
      }) as IOrganization,
    [data]
  )

  const addFeature = useCallback(
    async (id: FeatureId, settings?: Record<string, unknown>) =>
      mutate(APIClient.makeAPICall('POST', '/org/features', { body: { id, settings } }), {
        optimisticData: (data) => ({
          ...data,
          features: (data.features ?? []).concat({ id, settings }),
        }),
        revalidate: false,
      }),
    [mutate]
  )

  const getStripeHref = useCallback(async () => {
    const response = await APIClient.makeAPICall('POST', '/org/manageSubscription')
    if (!response) {
      throw new Error('Could not launch subscription manager')
    }

    return response
  }, [])

  // Force a real revalidation of the '/org/info' key. The local mutate() does
  // not always propagate to other hook instances (e.g. useTerminologyOptions
  // reads orgInfo.features), so also fire the GLOBAL mutate on the key. This
  // makes newly-persisted features/options appear without a manual refresh.
  const refresh = useCallback(async () => {
    await mutate(undefined, { revalidate: true })
    await globalMutate('/org/info', undefined, { revalidate: true })
  }, [mutate, globalMutate])

  const removeFeature = useCallback(
    async (id: string) =>
      mutate(APIClient.makeAPICall('DELETE', `/org/features/${id}`), {
        optimisticData: (data) => ({ ...data, features: data.features.filter((f) => f.id !== id) }),
        populateCache: false,
      }),
    [mutate]
  )

  const update = useCallback(
    async ({
      logo,
      name,
      documentTermSingular,
      documentTermPlural,
      tavusGreeting,
    }: UpdateOrgProps) =>
      mutate(
        async () => {
          setSaving(true)
          try {
            // The backend ignores undefined keys, so a partial update (e.g. just
            // terminology) leaves name/logo untouched.
            return APIClient.makeAPICall('PUT', '/org/info', {
              body: {
                orgName: name,
                logo,
                documentTermSingular,
                documentTermPlural,
                tavusGreeting,
              },
            })
          } finally {
            setSaving(false)
          }
        },
        { revalidate: false }
      ),
    [mutate]
  )

  const updateFeature = useCallback(
    (id: string, settings: Record<string, unknown>) =>
      mutate(
        async () => {
          setSaving(true)
          try {
            return APIClient.makeAPICall('PUT', `/org/features/${id}`, { body: { id, settings } })
          } finally {
            setSaving(false)
          }
        },
        { populateCache: false }
      ),
    [mutate]
  )

  const updateFeatures = useCallback(
    (features: IOrganizationFeature[]) =>
      mutate(
        async () => {
          setSaving(true)
          try {
            return APIClient.makeAPICall('PUT', `/org/features`, { body: features })
          } finally {
            setSaving(false)
          }
        },
        { populateCache: false }
      ),
    [mutate]
  )

  return {
    error,
    loading,
    orgInfo,
    saving,
    addFeature,
    getStripeHref,
    refresh,
    removeFeature,
    update,
    updateFeature,
    updateFeatures,
  }
}
